import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { AssetImage, firstError, HeaderStats, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { createDateFormatter, useDisplayTimezone } from "@nexus/ui";
import { SOCIAL_MEDIA_POSTS_CONTRACT } from "./types";
import type { SocialMediaMessage, SocialMediaPlatform, SocialMediaPostsData } from "./types";

export interface SocialMediaPostsConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "social-media-posts",
  name: "Posts on social media",
  description: "Social-media chatter observed around one protected event's piracy activity.",
  version: "1.0.0",
  dataRequests: {
    social: {
      source: "protection-overview-event-social",
      contract: SOCIAL_MEDIA_POSTS_CONTRACT,
      // The dashboard instance binds this to "protection-overview-event-social"
      // with a report_instance_id param (see domains.dashboards.builtins). A
      // dataBindings entry only takes effect when the request declares itself
      // configurable — "false" here silently produced "Data request does not
      // allow a source override" and no data ever loaded.
      configurable: true,
    },
  },
};

export const defaultConfig: SocialMediaPostsConfig = {};

const PLATFORM_ACCENT: Record<SocialMediaPlatform["platform"], string> = {
  x: "--data-detection",
  reddit: "--sev-high",
  telegram: "--data-response",
  facebook: "--data-impact",
};

function MessageCard({ message }: { message: SocialMediaMessage }) {
  const dateTimeFormat = createDateFormatter(
    useDisplayTimezone(),
    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
    "en",
  );
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-xs font-semibold text-primary">{message.author}</span>
        <span className="shrink-0 text-2xs text-muted">
          {dateTimeFormat.format(new Date(message.at))}
        </span>
      </div>
      <span className="text-2xs text-muted">{message.handle}</span>
      <p className="text-xs text-secondary">{message.text}</p>
    </div>
  );
}

function PlatformColumn({ platform }: { platform: SocialMediaPlatform }) {
  const accent = PLATFORM_ACCENT[platform.platform] ?? "--data-detection";

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        className="flex items-center gap-2 border-b-2 pb-1.5"
        style={{ borderColor: `var(${accent})` }}
      >
        <AssetImage reference={platform.logo} className="h-5 w-5 shrink-0 object-contain" />
        <span className="text-sm font-semibold text-primary">{platform.label}</span>
        <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-2xs font-medium text-muted">
          {platform.messageCount}
        </span>
      </div>
      <div className="flex max-h-96 flex-col gap-2 overflow-y-auto pr-1">
        {platform.messages.length === 0 ? (
          <p className="text-2xs text-muted">No posts captured.</p>
        ) : (
          platform.messages.map((message, index) => (
            <MessageCard key={`${platform.platform}:${index}`} message={message} />
          ))
        )}
      </div>
    </div>
  );
}

export function Component({ data, config }: WidgetProps<SocialMediaPostsConfig>) {
  const error = firstError(data, ["social"]);
  const result = unwrap<SocialMediaPostsData>(data.social);
  const loading = data.social?.isLoading && !result;
  const heading = config.title ?? manifest.name;

  return (
    <Widget
      title={heading}
      headerSummary={
        result ? (
          <HeaderStats
            label={`${heading} summary`}
            items={result.platforms.map((platform) => ({
              id: platform.platform,
              label: platform.label,
              value: platform.messageCount,
              format: "number",
            }))}
          />
        ) : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="list" />
      ) : result.platforms.length === 0 ? (
        <WidgetState title="No social-media activity captured for this event." />
      ) : (
        <div className="grid grid-cols-1 gap-4 density-widget-body @xl/widget:grid-cols-2 @6xl/widget:grid-cols-4">
          {result.platforms.map((platform) => (
            <PlatformColumn key={platform.platform} platform={platform} />
          ))}
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<SocialMediaPostsConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
